import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { ShareTarget, anchoredPosition, embedCode, shareTargets } from '../video-detail-share';

const COPIED_MS = 2000;
/** From this width the panel opens next to the share button; narrower screens get the bottom sheet. */
const ANCHORED_MIN_WIDTH = 600;

/** Share panel next to the share button (bottom sheet on phones): copy link, native share, social networks, embed code. */
@Component({
  selector: 'app-vd-share-sheet',
  templateUrl: './vd-share-sheet.component.html',
  styleUrls: ['./vd-share-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VdShareSheetComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() url = '';
  @Input() title = '';
  @Input() image: string | null = null;
  /** The share button that opened the panel; without it the panel is a centred dialog. */
  @Input() anchor: HTMLElement | null = null;
  @Output() close = new EventEmitter<void>();
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('sheet') sheet?: ElementRef<HTMLElement>;
  @HostBinding('class.is-anchored') anchored = false;

  readonly canNativeShare = typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';
  targets: ShareTarget[] = [];
  embed = '';
  showEmbed = false;
  copied: 'link' | 'embed' | null = null;
  private copiedTimer: any;
  private previousFocus: Element | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.targets = shareTargets(this.url, this.title);
    this.embed = embedCode(this.url);
    this.anchored = !!this.anchor && window.innerWidth >= ANCHORED_MIN_WIDTH;
  }

  ngAfterViewInit(): void {
    this.previousFocus = document.activeElement;
    this.place();
    this.closeButton?.nativeElement.focus({ preventScroll: true });
  }

  /** Keeps the panel next to its button while the page scrolls or resizes (the embed box also changes its height). */
  @HostListener('window:resize')
  @HostListener('window:scroll')
  place(): void {
    const sheet = this.sheet?.nativeElement;
    if (!this.anchored || !sheet || !this.anchor) return;
    const box = this.anchor.getBoundingClientRect();
    const spot = anchoredPosition(box, { width: sheet.offsetWidth, height: sheet.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight });
    sheet.style.top = `${spot.top}px`;
    sheet.style.left = `${spot.left}px`;
    sheet.classList.toggle('is-above', spot.above);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close.emit();
  }

  async copy(kind: 'link' | 'embed'): Promise<void> {
    const text = kind === 'link' ? this.url : this.embed;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const field = document.createElement('textarea');
      field.value = text;
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      document.body.removeChild(field);
    }
    this.copied = kind;
    this.cdr.markForCheck();
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => { this.copied = null; this.cdr.markForCheck(); }, COPIED_MS);
  }

  toggleEmbed(): void {
    this.showEmbed = !this.showEmbed;
    setTimeout(() => this.place());
  }

  nativeShare(): void {
    (navigator as any).share({ title: this.title, url: this.url }).catch(() => undefined);
  }

  ngOnDestroy(): void {
    clearTimeout(this.copiedTimer);
    (this.previousFocus as HTMLElement | null)?.focus?.();
  }
}
